import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import { useMaterialUIController } from "context";

const modalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: "1000px",
  maxHeight: "95vh",
  overflow: "auto",
  bgcolor: "background.paper",
  borderRadius: "8px",
  boxShadow: 24,
  p: 0,
};

function RegistrarCotizacion({ open, onClose, onSuccess }) {
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const empresaId = localStorage.getItem("empresaId") || "1";

  const initialFormState = {
    empresa_id: empresaId,
    proveedor_id: "",
    fecha_cotizacion: new Date().toISOString().split("T")[0],
    fecha_vigencia: "",
    folio: "",
    notas: "",
    estatus: "vigente",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [items, setItems] = useState([{ producto_id: "", cantidad: 1, precio_unitario: 0 }]);
  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
      setFormData({ ...initialFormState, empresa_id: empresaId });
      setItems([{ producto_id: "", cantidad: 1, precio_unitario: 0 }]);
    }
  }, [open]);

  const fetchData = async () => {
    try {
      const [provRes, prodRes] = await Promise.all([
        axios.get(API_ENDPOINTS.PROVEEDORES, { params: { empresa_id: empresaId } }),
        axios.get(API_ENDPOINTS.PRODUCTOS, { params: { empresa_id: empresaId } }),
      ]);
      setProveedores(provRes.data);
      setProductos(prodRes.data);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    setItems([...items, { producto_id: "", cantidad: 1, precio_unitario: 0 }]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Si cambia el producto, intentar poner el precio por defecto
    if (field === "producto_id") {
      const producto = productos.find((p) => p.id === value);
      if (producto) {
        newItems[index].precio_unitario = producto.costo || 0;
      }
    }

    setItems(newItems);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((acc, item) => acc + item.cantidad * item.precio_unitario, 0);
    const impuesto = subtotal * 0.16; // Asumiendo 16% IVA
    const total = subtotal + impuesto;
    return { subtotal, impuesto, total };
  };

  const totals = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.some((item) => !item.producto_id)) {
      alert("Por favor seleccione un producto para todos los items.");
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        ...totals,
        detalles: items,
      };
      await axios.post(API_ENDPOINTS.COTIZACIONES, dataToSend);
      alert("Cotización registrada exitosamente");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error al registrar cotización:", error);
      alert("Error al registrar cotización. Verifique los datos e intente de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyles}>
        <form onSubmit={handleSubmit}>
          <MDBox
            variant="gradient"
            bgColor={sidenavColor}
            borderRadius="8px 8px 0 0"
            p={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h5" color="white" fontWeight="medium">
              Nueva Cotización de Proveedor
            </MDTypography>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </MDBox>

          <MDBox p={3}>
            <Grid container spacing={3}>
              {/* Datos Generales */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Datos Generales</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth required>
                          <InputLabel>Proveedor</InputLabel>
                          <Select
                            name="proveedor_id"
                            value={formData.proveedor_id}
                            onChange={handleChange}
                            label="Proveedor"
                            sx={{ height: "45px" }}
                          >
                            {proveedores.map((prov) => (
                              <MenuItem key={prov.id} value={prov.id}>
                                {prov.nombre}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Folio/Referencia"
                          name="folio"
                          value={formData.folio}
                          onChange={handleChange}
                          placeholder="Ej: COT-2024-001"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          type="date"
                          label="Fecha Cotización"
                          name="fecha_cotizacion"
                          value={formData.fecha_cotizacion}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          type="date"
                          label="Fecha Vigencia"
                          name="fecha_vigencia"
                          value={formData.fecha_vigencia}
                          onChange={handleChange}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Items de la Cotización */}
              <Grid item xs={12}>
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Productos / Servicios</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead sx={{ display: "table-header-group" }}>
                          <TableRow>
                            <TableCell width="40%">Producto</TableCell>
                            <TableCell align="center" width="15%">
                              Cantidad
                            </TableCell>
                            <TableCell align="right" width="20%">
                              Precio Unit.
                            </TableCell>
                            <TableCell align="right" width="20%">
                              Subtotal
                            </TableCell>
                            <TableCell width="5%"></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <FormControl fullWidth size="small">
                                  <Select
                                    value={item.producto_id}
                                    onChange={(e) =>
                                      handleItemChange(index, "producto_id", e.target.value)
                                    }
                                  >
                                    {productos.map((prod) => (
                                      <MenuItem key={prod.id} value={prod.id}>
                                        {prod.nombre}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.cantidad}
                                  onChange={(e) =>
                                    handleItemChange(
                                      index,
                                      "cantidad",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  inputProps={{ min: 1 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.precio_unitario}
                                  onChange={(e) =>
                                    handleItemChange(
                                      index,
                                      "precio_unitario",
                                      parseFloat(e.target.value) || 0
                                    )
                                  }
                                  inputProps={{ min: 0, step: 0.01 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <MDTypography variant="button">
                                  $
                                  {(item.cantidad * item.precio_unitario).toLocaleString("es-MX", {
                                    minimumFractionDigits: 2,
                                  })}
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRemoveItem(index)}
                                  disabled={items.length === 1}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <MDBox mt={2}>
                      <MDButton
                        variant="outlined"
                        color={sidenavColor}
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                      >
                        Agregar Fila
                      </MDButton>
                    </MDBox>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Notas y Totales */}
              <Grid item xs={12} md={7}>
                <TextField
                  fullWidth
                  label="Notas / Observaciones"
                  name="notas"
                  multiline
                  rows={4}
                  value={formData.notas}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <MDBox p={2} bgColor="grey-100" borderRadius="lg">
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <MDTypography variant="button" color="text">
                        Subtotal:
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <MDTypography variant="button" fontWeight="bold">
                        ${totals.subtotal.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="button" color="text">
                        IVA (16%):
                      </MDTypography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <MDTypography variant="button" fontWeight="bold">
                        ${totals.impuesto.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </MDTypography>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider />
                    </Grid>
                    <Grid item xs={6}>
                      <MDTypography variant="h6">TOTAL:</MDTypography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <MDTypography variant="h6" color={sidenavColor}>
                        ${totals.total.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </MDTypography>
                    </Grid>
                  </Grid>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox p={3} display="flex" justifyContent="flex-end" gap={2}>
            <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
              Cancelar
            </MDButton>
            <MDButton type="submit" variant="gradient" color={sidenavColor} disabled={loading}>
              {loading ? "Registrando..." : "Guardar Cotización"}
            </MDButton>
          </MDBox>
        </form>
      </Box>
    </Modal>
  );
}

RegistrarCotizacion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default RegistrarCotizacion;
